import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDesign } from '../../designs';
import { useTrip } from '../../hooks/useTrip';
import { createCheckoutSession } from '../../services/sharing';
import { trackPaywallViewed } from '../../config/analytics';
import DesignButton from '../../components/ui/DesignButton';

export default function PaywallScreen() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const { data: trip } = useTrip(tripId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tripId) trackPaywallViewed(tripId);
  }, [tripId]);

  const handlePay = async () => {
    if (!tripId) return;
    setIsLoading(true);
    setError('');
    try {
      const checkoutUrl = await createCheckoutSession(tripId);
      window.location.href = checkoutUrl;
    } catch (err: unknown) {
      console.error('Checkout error:', err);
      const message = err instanceof Error ? err.message : 'Unable to start checkout. Please try again.';
      setError(message);
      setIsLoading(false);
    }
  };

  const meta = trip?.meta;

  // Format date range — same hero treatment as TripScreen
  const formatDateHero = () => {
    if (!meta?.startDate || !meta?.endDate) return '';
    const start = new Date(meta.startDate + 'T00:00:00');
    const end = new Date(meta.endDate + 'T00:00:00');
    const fmt = (dt: Date) => dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`.toUpperCase();
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '32px',
      backgroundColor: d.bgPrimary,
    }}>
      <div style={{ width: '100%', maxWidth: '380px', textAlign: 'center' }}>

        {/* ═══ TRIP PREVIEW ═══ */}
        {meta && (
          <div style={{ marginBottom: '32px' }}>
            {/* Occasion line */}
            {meta.occasion && (
              <div style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
                letterSpacing: '4px', textTransform: 'uppercase',
                color: 'rgba(200,146,58,0.40)',
                marginBottom: '10px',
              }}>{meta.occasion}</div>
            )}

            {/* Trip title */}
            <h1 style={{
              fontFamily: displayFont, fontSize: '44px', fontWeight: 700,
              lineHeight: 0.9, color: d.textPrimary, margin: '0 0 8px',
            }}>{meta.tripName}</h1>

            {/* City */}
            <div style={{
              fontFamily: displayFont, fontSize: '18px', fontWeight: 400,
              fontStyle: 'italic', color: d.accent, marginBottom: '12px',
            }}>{meta.destination}</div>

            {/* Date line with flanking rules */}
            {formatDateHero() && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '12px',
              }}>
                <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200,146,58,0.25)' }} />
                <span style={{
                  fontFamily: displayFont, fontSize: '13px', fontWeight: 600,
                  letterSpacing: '2px', color: 'rgba(237,228,216,0.60)',
                }}>{formatDateHero()}</span>
                <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200,146,58,0.25)' }} />
              </div>
            )}
          </div>
        )}

        {/* ═══ VALUE DESCRIPTION ═══ */}
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
          color: d.textSecondary, lineHeight: 1.6, margin: '0 0 32px',
        }}>
          When you share, your crew can add travel, build the itinerary with you, and split expenses — all in one place.
        </p>

        {/* ═══ PRICE ═══ */}
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.screenHeader, fontWeight: 400,
          color: d.textPrimary, margin: '0 0 4px',
        }}>
          $30
        </p>
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, margin: '0 0 28px',
        }}>
          One-time payment · All future updates included
        </p>

        {/* ═══ ERROR ═══ */}
        {error && (
          <p style={{
            color: d.negative, fontFamily: "'Raleway', sans-serif",
            fontSize: fs.detail, marginBottom: '16px',
          }}>
            {error}
          </p>
        )}

        {/* ═══ CTA ═══ */}
        <DesignButton tier={2} fullWidth onClick={handlePay} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'SHARE FOR $30'}
        </DesignButton>

        {/* ═══ BACK LINK ═══ */}
        <button onClick={() => navigate(`/trip/${tripId}`)} type="button" style={{
          background: 'none', border: 'none', cursor: 'pointer', marginTop: '24px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary,
        }}>
          ← Back to trip
        </button>
      </div>
    </div>
  );
}
